'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Link as MuiLink, Tooltip, Typography } from '@unity/cloud-ui';
import z from 'zod';
import { getComponentDocumentationUrl } from '@/utils/get-component-doc-url';

export interface GlossaryTermProps {
  term: string;
  definition?: string;
  moreInfoUrl?: string;
  displayText?: string;
}

export const GlossaryTerm = (props: GlossaryTermProps) => {
  const t = useTranslations();

  if (!props.term) {
    return null;
  }

  const tooltipContent = (
    <>
      <Typography component="span" fontWeight="bold">
        {props.term}
      </Typography>
      <Typography component="p">{props.definition}</Typography>
      {props.moreInfoUrl && (
        <MuiLink component={Link} href={props.moreInfoUrl} external>
          {t('mdx:glossary-term:more-info')}
        </MuiLink>
      )}
    </>
  );

  return (
    <Tooltip arrow title={tooltipContent} placement="top">
      <Typography component="span" fontWeight="bold" sx={{ textDecoration: 'underline', width: 'max-content' }}>
        {props.displayText || props.term}
      </Typography>
    </Tooltip>
  );
};

const documentationURL = getComponentDocumentationUrl('glossary-terms');

const descriptions = {
  src: 'The source path of the glossary term YAML file. The path must be prefixed with a leading slash character: / and must retain the extension .yaml. The locale of the linked glossary term will be inferred from the page that contains the reference.',
  term: 'The Glossary Term whose tooltip you would like to show. The term will be displayed with the casing that is provided in this prop.',
  label:
    'The context from which to draw the definition of the term. A term can have different definitions based on the context where it is used, in which case the label is very important to distinguish those. Even if the term is unique enough where no other contexts are present in its definition, it is still required to define a label and use it.',
  displayText:
    'An override of the text over which the tooltip will be displayed. Common use case for this prop is to use it pluralize the glossary word. i.e Show the tooltip for `Child` when hovering the word `Children`',
  schema: `Display a glossary term with a definition. 
  
  
  **Example**
  
  \`\`\`mdx  
<GlossaryTerm
  term="Child"
  displayText="Children"
  label="Demo"
/>
\`\`\`

${documentationURL}`,
};

const srcOnly = z.object({
  src: z.string(),
});

const termAndLabel = z.object({
  term: z.string(),
  label: z.string(),
  displayText: z.string().optional(),
});

export const glossaryTermSchema = z.union([srcOnly, termAndLabel]).describe(descriptions.schema);
